import React, { useMemo, useState } from "react"
import { useQuery } from "@apollo/client"
import {
    Avatar,
    Box,
    CssBaseline,
    List,
    ListItem,
    Typography,
    useTheme,
    TextField,
    InputAdornment,
    IconButton,
    Button,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded"
import { LIST_CONVERSATIONS } from "../../entities/conversation/graphql/queries"

// Define types for conversation structure
interface ConversationMetadata {
    updatedAt: string
    updatedBy: {
        name: string
    }
}

interface Conversation {
    _id: string
    title: string | null
    _metadata?: ConversationMetadata
    users: string[]
}

// Define type for GraphQL query response
interface ConversationsData {
    conversationsList: Conversation[]
}

// Responsive height styles
const pageStyles = {
    height: { xs: "90vh", sm: "89vh", md: "88vh" },
    "@media (max-height: 600px)": { height: "87vh" },
    "@media (max-height: 500px)": { height: "85vh" },
    "@media (max-height: 400px)": { height: "82vh" },
    "@media (max-height: 300px)": { height: "79vh" },
}

// Custom styles
const styles = {
    listContainer: {
        width: "100%",
        overflow: "auto",
        "&::-webkit-scrollbar": { display: "none", width: "0px" },
        marginTop: 1.5,
    },
    searchInput: {
        width: "100%",
        height: "50px",
        bgcolor: "#103751",
        borderRadius: "25px",
        "& .MuiOutlinedInput-root": {
            paddingRight: "10px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            "& fieldset": { border: "none" },
        },
        "& .MuiInputBase-input": {
            color: "white",
            padding: "14px 16px",
            fontSize: "16px",
        },
    },
    searchIconButton: {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        borderRadius: "50%",
        width: "36px",
        height: "36px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
        },
    },
    noConversationBox: {
        width: "100%",
        height: "70vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}

const Conversation: React.FC = () => {
    const selectedSite = sessionStorage.getItem("selectedSite")
    const {
        data: ConversationData,
        loading,
        error,
    } = useQuery<ConversationsData>(LIST_CONVERSATIONS, { skip: !selectedSite })
    const theme = useTheme()
    const [searchQuery, setSearchQuery] = useState("")

    // Memoize conversations list to prevent unnecessary re-renders
    const conversations = useMemo(() => {
        if (!ConversationData?.conversationsList) return []
        // Filter based on search query
        return ConversationData.conversationsList.filter((conversation) =>
            (conversation.title || "Untitled").toLowerCase().includes(searchQuery.toLowerCase())
        )
    }, [ConversationData, searchQuery])

    if (loading) return <Typography>Loading...</Typography>
    if (error) return <Typography color="error">Error: {error.message}</Typography>

    return (
        <Box
            width="100%"
            display="flex"
            bgcolor={theme.palette.background.default}
            justifyContent="center"
            sx={pageStyles}
        >
            <CssBaseline />
            <Box
                width="100%"
                maxWidth={700}
                display="flex"
                flexDirection="column"
                paddingTop={{ xs: "10%", sm: "7%", md: "3%" }}
                paddingX={{ xs: "6%", sm: "4%", md: "2%" }}
            >
                {/* Search Bar */}
                <TextField
                    placeholder="Search Conversations"
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton sx={styles.searchIconButton}>
                                    <SearchIcon sx={{ color: "white", fontSize: 22 }} />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={styles.searchInput}
                />

                {/* Conversations List */}
                <Box sx={styles.listContainer}>
                    {conversations.length === 0 ? (
                        <Box sx={styles.noConversationBox}>
                            <Typography variant="subtitle1" color={theme.palette.text.secondary} textAlign="center">
                                No Conversations Available
                            </Typography>
                        </Box>
                    ) : (
                        <List>
                            {conversations.map((conversation) => (
                                <ListItem
                                    key={conversation._id}
                                    disableGutters
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "flex-start",
                                        marginTop: 1,
                                    }}
                                >
                                    <Box
                                        width="100%"
                                        height="100%"
                                        display="flex"
                                        flexDirection="row"
                                        paddingX={1}
                                        alignItems="center"
                                    >
                                        {/* Avatar */}
                                        <Avatar
                                            sx={{
                                                height: { xs: "50px", sm: "60px", md: "70px" },
                                                width: { xs: "50px", sm: "60px", md: "70px" },
                                                marginRight: 2,
                                                boxShadow: 15,
                                            }}
                                        >
                                            <Typography variant="h6">
                                                {conversation?._metadata?.updatedBy?.name?.charAt(0) || "C"}
                                            </Typography>
                                        </Avatar>

                                        {/* Conversation Content */}
                                        <Box display="flex" flexDirection="column">
                                            <Typography variant="subtitle1">
                                                {conversation?._metadata?.updatedBy.name || "Untitled"}
                                            </Typography>
                                            <Typography variant="body2">
                                                {conversation.title || "No messages"}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Conversation Date */}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            width: "20%",
                                            textAlign: "end",
                                            paddingTop: 1.5,
                                        }}
                                    >
                                        {conversation._metadata?.updatedAt
                                            ? new Date(conversation?._metadata?.updatedAt).toLocaleDateString()
                                            : "N/A"}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>

                <Button
                    variant="contained"
                    sx={{
                        display: "flex",
                        position: "absolute",
                        bottom: 90,
                        right: 20,
                        zIndex: 10,
                        backgroundColor: "#26465b",
                        borderRadius: 3,
                    }}
                >
                    <AddCircleRoundedIcon />
                </Button>
            </Box>
        </Box>
    )
}

export default Conversation
